import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'

import { useNotificationPreferences } from './useNotificationPreferences'

const Overlay = styled(Dialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
  z-index: 1000;
`

const Content = styled(Dialog.Content)`
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.5rem;
  box-shadow:
    0 0.625rem 2.375rem -0.625rem rgba(22, 23, 24, 0.35),
    0 0.625rem 1.25rem -0.9375rem rgba(22, 23, 24, 0.2);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 24rem;
  max-height: 85%;
  padding: 1.5rem;
  z-index: 1001;
`

const Title = styled(Dialog.Title)`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
  margin-bottom: 1.5rem;
`

const CloseButton = styled(Dialog.Close)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.text3};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    color: ${({ theme }) => theme.forecast.text1};
  }
`

const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 0.0625rem solid ${({ theme }) => theme.forecast.border};

  &:last-child {
    border-bottom: none;
  }
`

const PreferenceLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const PreferenceTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.text1};
`

const PreferenceDescription = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.text3};
`

const Slider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.forecast.background3};
  transition: 0.2s;
  border-radius: 1.5rem;
  pointer-events: none;
  z-index: 1;

  &:before {
    position: absolute;
    content: '';
    height: 1.125rem;
    width: 1.125rem;
    left: 0.1875rem;
    bottom: 0.1875rem;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
  }
`

const ToggleSwitch = styled.label<{ $checked: boolean }>`
  position: relative;
  display: inline-block;
  width: 2.75rem;
  height: 1.5rem;
  cursor: pointer;
  user-select: none;

  input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    cursor: pointer;
    z-index: 2;
  }

  input:checked + ${Slider} {
    background-color: ${({ theme }) =>
      theme.forecast.primary ?? theme.forecast.defaultButton};
  }

  input:checked + ${Slider}:before {
    transform: translateX(1.25rem);
  }

  &:hover ${Slider} {
    background-color: ${({ theme, $checked }) =>
      $checked
        ? (theme.forecast.primary ?? theme.forecast.defaultButton)
        : theme.forecast.background4};
  }
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${({ $primary, theme }) =>
    $primary ? 'none' : `0.0625rem solid ${theme.forecast.border}`};
  background-color: ${({ $primary, theme }) =>
    $primary
      ? (theme.forecast.primary ?? theme.forecast.defaultButton)
      : 'transparent'};
  color: ${({ $primary, theme }) =>
    $primary ? 'white' : theme.forecast.text1};

  &:hover:not(:disabled) {
    background-color: ${({ $primary, theme }) =>
      $primary ? theme.forecast.hoverButton : theme.forecast.background3};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

interface NotificationPreferencesModalProps {
  isOpen: boolean
  onClose: () => void
}

type UiPrefs = { inapp: boolean; email: boolean }

export const NotificationPreferencesModal: React.FC<
  NotificationPreferencesModalProps
> = ({ isOpen, onClose }) => {
  const { preferences, isLoading, hasLoaded, updatePreferences, error } =
    useNotificationPreferences({
      enabled: isOpen,
    })

  const [ui, setUi] = useState<UiPrefs | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  React.useEffect(() => {
    if (hasLoaded && !error && isOpen && ui === null) {
      setUi({
        inapp: preferences.channels.inapp,
        email: preferences.channels.email,
      })
    }
  }, [preferences, hasLoaded, error, isOpen, ui])

  React.useEffect(() => {
    if (!isOpen) {
      setUi(null)
    }
  }, [isOpen])

  const handleToggle =
    (key: keyof UiPrefs) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked
      setUi((prev) => {
        if (!prev) {
          return {
            inapp: key === 'inapp' ? checked : true,
            email: key === 'email' ? checked : true,
          }
        }
        return { ...prev, [key]: checked }
      })
    }

  const handleSave = async () => {
    if (!ui) return
    setIsSaving(true)
    try {
      await updatePreferences(ui)
      onClose()
    } catch (e) {
      console.error('Failed to update notification preferences:', e)
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Overlay />
        <Content>
          <Title>Manage Notifications</Title>
          <CloseButton aria-label='Close'>
            <X size={16} strokeWidth={2} />
          </CloseButton>

          {isLoading || !hasLoaded || ui === null ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              Loading preferences...
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              Couldn&apos;t load preferences. Please try again.
            </div>
          ) : (
            <>
              <PreferenceRow>
                <PreferenceLabel>
                  <PreferenceTitle>Platform Notifications</PreferenceTitle>
                  <PreferenceDescription>
                    Show notifications in Cantilever
                  </PreferenceDescription>
                </PreferenceLabel>

                <ToggleSwitch $checked={ui.inapp}>
                  <input
                    type='checkbox'
                    checked={ui.inapp}
                    onChange={handleToggle('inapp')}
                  />
                  <Slider />
                </ToggleSwitch>
              </PreferenceRow>

              <PreferenceRow>
                <PreferenceLabel>
                  <PreferenceTitle>Email Notifications</PreferenceTitle>
                  <PreferenceDescription>
                    Receive notifications via email
                  </PreferenceDescription>
                </PreferenceLabel>

                <ToggleSwitch $checked={ui.email}>
                  <input
                    type='checkbox'
                    checked={ui.email}
                    onChange={handleToggle('email')}
                  />
                  <Slider />
                </ToggleSwitch>
              </PreferenceRow>

              <ModalActions>
                <ModalButton onClick={onClose} type='button'>
                  Cancel
                </ModalButton>
                <ModalButton
                  onClick={handleSave}
                  disabled={isSaving}
                  $primary
                  type='button'>
                  {isSaving ? 'Saving...' : 'Save'}
                </ModalButton>
              </ModalActions>
            </>
          )}
        </Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
